import { useNavigate } from 'react-router-dom';
import { Users, TrendingUp, Target, Trophy, CalendarDays } from 'lucide-react';
import { StatsCard } from '@/components/coach/stats-card';
import { SessionList } from '@/components/coach/session-list';
import { useCoachStats, useTodaySessions } from '@/hooks/use-coach-data';
import { useCoachAnnouncements } from '@/hooks/use-announcements';
import { AnnouncementsBanner } from '@/components/shared/announcements-banner';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
export default function CoachDashboard() {
  const navigate = useNavigate();
  const { data: stats, isLoading: statsLoading } = useCoachStats();
  const { data: sessionsData, isLoading: sessionsLoading } = useTodaySessions();
  const { data: announcementsData, isLoading: announcementsLoading } = useCoachAnnouncements();

  const handleSessionClick = (session: { id: string }) => {
    navigate(`/coach/sessions/${session.id}/attendance`);
  };

  const sessionCount = sessionsData?.sessions?.length ?? 0;

  return (
    <div className="space-y-6">
      {/* Today's Schedule — hero section */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <div className="flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-muted-foreground" />
            <CardTitle className="text-base">Today's Schedule</CardTitle>
          </div>
          {!sessionsLoading && sessionCount > 0 && (
            <Badge variant="secondary">{sessionCount} session{sessionCount !== 1 ? 's' : ''}</Badge>
          )}
        </CardHeader>
        <CardContent className="pt-0">
          {sessionsLoading ? (
            <div className="space-y-3">
              {[1, 2].map(i => <Skeleton key={i} className="h-16 w-full" />)}
            </div>
          ) : sessionCount === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <CalendarDays className="w-10 h-10 text-muted-foreground/30 mb-3" />
              <p className="text-sm font-medium text-foreground">No sessions today</p>
              <p className="text-xs text-muted-foreground mt-1 mb-3">Check the calendar to see upcoming sessions</p>
              <Link to="/coach/calendar" className="text-sm text-primary hover:underline">
                View calendar →
              </Link>
            </div>
          ) : (
            <SessionList
              sessions={sessionsData?.sessions ?? []}
              isLoading={false}
              onSessionClick={handleSessionClick}
            />
          )}
        </CardContent>
      </Card>

      {/* Announcements — only shown when there's something to show */}
      {(announcementsLoading || (announcementsData?.announcements?.length ?? 0) > 0) && (
        <AnnouncementsBanner
          announcements={announcementsData?.announcements ?? []}
          isLoading={announcementsLoading}
          messagesLink="/coach/messages"
          variant="coach"
        />
      )}

      {/* Stats — secondary row */}
      <div className="@container">
        <div className="grid grid-cols-2 @lg:grid-cols-4 gap-4">
          {statsLoading ? (
            <>
              {[1, 2, 3, 4].map(i => (
                <Skeleton key={i} className="h-24 w-full" />
              ))}
            </>
          ) : (
            <>
              <StatsCard
                icon={<Users className="w-8 h-8" />}
                value={stats?.totalPlayers ?? 0}
                label="Total Players"
                color="blue"
              />
              <StatsCard
                icon={<TrendingUp className="w-8 h-8" />}
                value={`${stats?.avgAttendance ?? 0}%`}
                label="Avg Attendance"
                color="green"
              />
              <StatsCard
                icon={<Target className="w-8 h-8" />}
                value={stats?.activeGoals ?? 0}
                label="Active Goals"
                color="purple"
              />
              <StatsCard
                icon={<Trophy className="w-8 h-8" />}
                value={stats?.matchesThisWeek ?? 0}
                label="Sessions This Week"
                color="orange"
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
